import { useCallback, useState } from "react";
import useAuth from "../context/useAuth.js";
import profileApi from "../services/profile.api.js";
import { forgetLogin, rememberLogin } from "../utils/savedLogins.js";

/**
 * The "Save Login Info on this Device" preference, shared by the header user
 * menu and the profile page.
 *
 * The toggle updates `AuthContext` + `localStorage` optimistically so the UI
 * reacts instantly, then persists to the server and rolls back if the write
 * fails. `localStorage` holds only the non-sensitive identifier.
 */
export default function useSaveLoginInfo() {
  const { user, updateUser } = useAuth();
  const [pending, setPending] = useState(false);
  const enabled = user?.preferences?.saveLoginInfo !== false;

  const setEnabled = useCallback(
    async (next) => {
      if (!user) return;
      setPending(true);
      updateUser({
        preferences: { ...(user.preferences || {}), saveLoginInfo: next },
      });
      if (next) {
        rememberLogin({
          email: user.email,
          name: user.name,
          role: user.role,
          avatar: user.avatar,
        });
      } else {
        forgetLogin(user.email);
      }

      try {
        await profileApi.updateMe({ preferences: { saveLoginInfo: next } });
      } catch {
        updateUser({
          preferences: { ...(user.preferences || {}), saveLoginInfo: !next },
        });
        if (next) forgetLogin(user.email);
        else {
          rememberLogin({
            email: user.email,
            name: user.name,
            role: user.role,
            avatar: user.avatar,
          });
        }
      } finally {
        setPending(false);
      }
    },
    [user, updateUser],
  );

  return { enabled, pending, setEnabled };
}
